<template>
  <b-modal
    id="image-annotator"
    ref="annotatorModal"
    size="xl"
    centered
    no-close-on-backdrop
    no-close-on-esc
    hide-header
    hide-footer
    body-class="p-0"
    modal-class="image-annotator-modal"
    @shown="onModalShown"
  >
    <div class="annotator-container">
      <div class="annotator-toolbar d-flex align-items-center justify-content-between px-3 py-2">
        <div class="d-flex align-items-center flex-wrap" style="gap: 12px">
          <b-button-group size="sm">
            <b-button
              :variant="tool === 'draw' ? 'primary' : 'outline-primary'"
              @click="setTool('draw')"
              title="Dibujar"
            >
              <i class="ri-pencil-line"></i>
            </b-button>
            <b-button
              :variant="tool === 'eraser' ? 'primary' : 'outline-primary'"
              @click="setTool('eraser')"
              title="Borrador"
            >
              <i class="ri-eraser-line"></i>
            </b-button>
          </b-button-group>
          <div class="d-flex align-items-center mx-2">
            <label class="text-nowrap mr-1 mb-0 small text-muted">Grosor</label>
            <b-form-input
              v-model.number="brushSize"
              type="range"
              min="1"
              max="30"
              size="sm"
              style="width: 80px"
              @input="updateBrushSize"
            />
          </div>
          <div class="d-flex align-items-center">
            <label class="text-nowrap mr-1 mb-0 small text-muted">Color</label>
            <input
              type="color"
              v-model="brushColor"
              class="annotator-color-picker"
              @input="updateBrushColor"
            />
          </div>
        </div>
        <div class="d-flex align-items-center flex-wrap" style="gap: 8px">
          <b-button size="sm" variant="outline-info" @click="undo" :disabled="!canUndo" title="Deshacer" class="ml-2">
            <i class="ri-arrow-go-back-line"></i>
          </b-button>
          <b-button size="sm" variant="outline-danger" @click="clearCanvas" title="Limpiar">
            <i class="ri-delete-bin-line"></i>
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="cancel" title="Cancelar">
            Cancelar
          </b-button>
          <b-button size="sm" variant="success" @click="save" :disabled="saving">
            <b-spinner v-if="saving" small class="mr-1"></b-spinner>
            Guardar
          </b-button>
        </div>
      </div>
      <div class="annotator-canvas-wrapper" ref="canvasWrapper">
        <canvas ref="fabricCanvas"></canvas>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { fabric } from "fabric";

export default {
  name: "ImageAnnotator",
  data() {
    return {
      fabricCanvas: null,
      tool: "draw",
      brushSize: 4,
      brushColor: "#FF0000",
      canUndo: false,
      saving: false,
      imageFile: null,
      history: [],
      historyIndex: -1,
      maxHistory: 30,
      eraserActive: false,
      eraserLastPoint: null,
      drawActive: false,
      drawLastPoint: null,
      strokesImage: null,
    };
  },
  methods: {
    open(file) {
      this.imageFile = file;
      this.history = [];
      this.historyIndex = -1;
      this.canUndo = false;
      this.eraserActive = false;
      this.eraserLastPoint = null;
      this.drawActive = false;
      this.drawLastPoint = null;
      this.strokesImage = null;
      this.$bvModal.show("image-annotator");
    },
    onModalShown() {
      this.initCanvas();
    },
    initCanvas() {
      if (this.fabricCanvas) {
        this.fabricCanvas.dispose();
      }

      const wrapper = this.$refs.canvasWrapper;
      const maxW = wrapper.clientWidth;
      const maxH = wrapper.clientHeight || window.innerHeight - 100;

      this.fabricCanvas = new fabric.Canvas(this.$refs.fabricCanvas, {
        isDrawingMode: true,
        selection: false,
        backgroundColor: "#fff",
      });

      const imgURL = URL.createObjectURL(this.imageFile);
      fabric.Image.fromURL(imgURL, (img) => {
        URL.revokeObjectURL(imgURL);

        let scale = 1;
        if (img.width > maxW || img.height > maxH) {
          scale = Math.min(maxW / img.width, maxH / img.height);
        }

        const w = img.width * scale;
        const h = img.height * scale;

        this.fabricCanvas.setDimensions({ width: w, height: h });
        this.fabricCanvas.setBackgroundImage(img, () => {
          this.fabricCanvas.renderAll();
        }, {
          scaleX: scale,
          scaleY: scale,
          originX: "left",
          originY: "top",
        });

        this.fabricCanvas.freeDrawingBrush.color = this.brushColor;
        this.fabricCanvas.freeDrawingBrush.width = this.brushSize;

        this.fabricCanvas.on("path:created", () => {
          this.pushHistory();
        });

        this.fabricCanvas.on("mouse:down", (o) => {
          const p = this.fabricCanvas.getPointer(o.e);
          if (this.tool === "eraser") {
            this.eraserActive = true;
            this.eraserLastPoint = p;
            this.rasterizeStrokes();
            if (this.strokesImage) {
              this.eraseDot(p);
            }
          } else if (this.tool === "draw") {
            this.drawActive = true;
            this.drawLastPoint = p;
            this.rasterizeStrokes();
            this.drawDot(p);
          }
        });

        this.fabricCanvas.on("mouse:move", (o) => {
          const p = this.fabricCanvas.getPointer(o.e);
          if (this.tool === "eraser" && this.eraserActive) {
            this.eraseSegment(this.eraserLastPoint, p);
            this.eraserLastPoint = p;
          } else if (this.tool === "draw" && this.drawActive) {
            this.drawSegment(this.drawLastPoint, p);
            this.drawLastPoint = p;
          }
        });

        this.fabricCanvas.on("mouse:up", () => {
          if (this.tool === "eraser" && this.eraserActive) {
            this.eraserActive = false;
            this.eraserLastPoint = null;
            this.pushHistory();
          } else if (this.tool === "draw" && this.drawActive) {
            this.drawActive = false;
            this.drawLastPoint = null;
            this.pushHistory();
          }
        });

        this.pushHistory();
      });
    },
    setTool(t) {
      this.tool = t;
      this.fabricCanvas.isDrawingMode = false;
      if (t === "draw") {
        this.fabricCanvas.freeDrawingBrush.color = this.brushColor;
        this.fabricCanvas.freeDrawingBrush.width = this.brushSize;
      }
    },
    updateBrushSize() {
      if (!this.fabricCanvas) return;
      const w = this.tool === "eraser" ? this.brushSize * 3 : this.brushSize;
      this.fabricCanvas.freeDrawingBrush.width = w;
    },
    updateBrushColor() {
      if (!this.fabricCanvas) return;
      if (this.tool === "draw") {
        this.fabricCanvas.freeDrawingBrush.color = this.brushColor;
      }
    },
    rasterizeStrokes() {
      const paths = this.fabricCanvas
        .getObjects()
        .filter((obj) => obj.type === "path");
      if (!paths.length) {
        if (!this.strokesImage) {
          this.ensureStrokesImage();
        }
        return;
      }

      let element;
      if (this.strokesImage) {
        element = this.strokesImage._originalElement;
      } else {
        element = this.ensureStrokesImage();
      }

      const ctx = element.getContext("2d");
      paths.forEach((obj) => obj.render(ctx));
      paths.forEach((obj) => this.fabricCanvas.remove(obj));
      this.strokesImage.dirty = true;
      this.fabricCanvas.requestRenderAll();
    },
    ensureStrokesImage() {
      if (this.strokesImage) {
        return this.strokesImage._originalElement;
      }
      const element = document.createElement("canvas");
      element.width = this.fabricCanvas.getWidth();
      element.height = this.fabricCanvas.getHeight();
      this.strokesImage = new fabric.Image(element);
      this.strokesImage.set({
        left: 0,
        top: 0,
        originX: "left",
        originY: "top",
        selectable: false,
        evented: false,
      });
      this.fabricCanvas.add(this.strokesImage);
      return element;
    },
    drawDot(point) {
      const element = this.strokesImage._originalElement;
      const ctx = element.getContext("2d");
      ctx.save();
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = this.brushColor;
      ctx.beginPath();
      ctx.arc(point.x, point.y, this.brushSize / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      this.strokesImage.dirty = true;
      this.fabricCanvas.requestRenderAll();
    },
    drawSegment(from, to) {
      const element = this.strokesImage._originalElement;
      const ctx = element.getContext("2d");
      ctx.save();
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = this.brushColor;
      ctx.lineWidth = this.brushSize;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
      ctx.stroke();
      ctx.restore();
      this.strokesImage.dirty = true;
      this.fabricCanvas.requestRenderAll();
    },
    eraseDot(point) {
      const element = this.strokesImage._originalElement;
      const ctx = element.getContext("2d");
      const width = this.brushSize * 3;
      ctx.save();
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "#000";
      ctx.beginPath();
      ctx.arc(point.x, point.y, width / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      this.strokesImage.dirty = true;
      this.fabricCanvas.requestRenderAll();
    },
    eraseSegment(from, to) {
      const element = this.strokesImage._originalElement;
      const ctx = element.getContext("2d");
      const width = this.brushSize * 3;
      ctx.save();
      ctx.globalCompositeOperation = "destination-out";
      ctx.lineWidth = width;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#000";
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
      ctx.stroke();
      ctx.restore();
      this.strokesImage.dirty = true;
      this.fabricCanvas.requestRenderAll();
    },
    pushHistory() {
      const json = this.fabricCanvas.toJSON(["selectable", "evented"]);
      this.history = this.history.slice(0, this.historyIndex + 1);
      this.history.push(JSON.stringify(json));
      if (this.history.length > this.maxHistory) {
        this.history.shift();
      }
      this.historyIndex = this.history.length - 1;
      this.canUndo = this.historyIndex > 0;
    },
    undo() {
      if (this.historyIndex <= 0) return;
      this.historyIndex--;
      this.fabricCanvas.loadFromJSON(
        this.history[this.historyIndex],
        () => {
          this.fabricCanvas.renderAll();
          this.canUndo = this.historyIndex > 0;
          this.strokesImage = this.fabricCanvas
            .getObjects()
            .find((obj) => obj.type === "image") || null;
        }
      );
    },
    clearCanvas() {
      const bg = this.fabricCanvas.backgroundImage;
      this.fabricCanvas.clear();
      this.fabricCanvas.setBackgroundImage(bg, () => {
        this.fabricCanvas.renderAll();
      });
      this.strokesImage = null;
      this.pushHistory();
    },
    cancel() {
      this.$bvModal.hide("image-annotator");
      this.$emit("cancel");
    },
    save() {
      if (!this.fabricCanvas) return;
      this.saving = true;

      this.fabricCanvas.isDrawingMode = false;

      const dataURL = this.fabricCanvas.toDataURL({
        format: "png",
        quality: 1,
        multiplier: 2,
      });

      this.dataURLtoBlob(dataURL).then((blob) => {
        const annotatedFile = new File(
          [blob],
          this.imageFile.name || "annotated.png",
          { type: "image/png" }
        );

        this.$emit("save", annotatedFile);
        this.$bvModal.hide("image-annotator");
        this.saving = false;
      });
    },
    dataURLtoBlob(dataURL) {
      return new Promise((resolve) => {
        const parts = dataURL.split(",");
        const mime = parts[0].match(/:(.*?);/)[1];
        const b64 = atob(parts[1]);
        const u8 = new Uint8Array(b64.length);
        for (let i = 0; i < b64.length; i++) {
          u8[i] = b64.charCodeAt(i);
        }
        resolve(new Blob([u8], { type: mime }));
      });
    },
  },
  beforeDestroy() {
    if (this.fabricCanvas) {
      this.fabricCanvas.dispose();
      this.fabricCanvas = null;
    }
  },
};
</script>

<style>
.image-annotator-modal .annotator-container {
  display: flex;
  flex-direction: column;
  height: 85vh;
  background: #f8f9fa;
}

.image-annotator-modal .annotator-toolbar {
  background: #fff;
  border-bottom: 1px solid #dfe6e9;
  flex-shrink: 0;
}

.image-annotator-modal .annotator-color-picker {
  width: 32px;
  height: 28px;
  border: 1px solid #b2bec3;
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
  background: none;
}

.image-annotator-modal .annotator-canvas-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  padding: 8px;
}

.image-annotator-modal .annotator-canvas-wrapper canvas {
  max-width: 100%;
  max-height: 100%;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.image-annotator-modal .modal-dialog {
  max-width: 95vw;
  margin: 2vh auto;
}

.image-annotator-modal .modal-content {
  background: #f8f9fa;
  border: none;
}

.image-annotator-modal .modal-body {
  padding: 0;
  overflow: hidden;
}
</style>
